import React, { useState, useMemo, useRef } from 'react';
import { Image, ArrowLeft, Play, Pause } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import MediaRenderer from './MediaRenderer';
import InfiniteScrollContainer from '../../UI/InfiniteScrollContainer';
import { templatesAPI } from '../../../services/api';

/**
 * TemplateComponents - Manages template display and component viewing
 * Handles both template list and individual template component views
 */
const TemplateComponents = ({
  onItemDoubleClick,
  onDragStart,
  searchQuery = '',
  className = '',
}) => {
  const [showTemplateComponents, setShowTemplateComponents] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [playingVideo, setPlayingVideo] = useState(null);
  const [hoveredTemplate, setHoveredTemplate] = useState(null);
  const videoRefs = useRef({});

  // Fetch templates from the database
  const { data: templatesData, isLoading: templatesLoading } = useQuery({
    queryKey: ['templates'],
    queryFn: () => templatesAPI.getAll(),
  });

  // Extract templates and transform them to include component URLs from layers
  const templateItems = useMemo(() => {
    const templates = templatesData?.data?.data?.templates || templatesData?.data?.templates || [];
    
    return templates.map(template => ({
      id: template._id,
      name: template.name,
      thumbnail: template.thumbnailUrl,
      previewUrl: template.previewVideoUrl, // Add preview URL for video
      category: template.category || 'other',
      description: template.description,
      canvas: template.canvas, // Include canvas size information
      layers: template.layers || [],
      // Extract components from layers with full componentData (including fields)
      // Only include layers that have fields (AG components)
      components: (template.layers || []).map(layer => {
        // Get the full componentData which includes fields for AG components
        const componentData = layer.componentData || {};
        
        return {
          // Don't include layer.id - let the system generate new IDs when adding to canvas
          name: layer.name || 'Component',
          url: componentData.currSrc || componentData.preview || componentData.url || null,
          type: layer.type,
          // Spread the full componentData to preserve fields and other properties
          ...componentData
        };
      }).filter(comp => comp.url && comp.fields && comp.fields.length > 0) // Only include AG components with fields
    }));
  }, [templatesData]);

  // Filter templates based on search query
  const filteredTemplates = useMemo(() => {
    return templateItems.filter(
      (template) =>
        !searchQuery ||
        template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        template.category.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [templateItems, searchQuery]);

  // Handle drag start for templates
  const handleDragStart = (event, elementData) => {
    if (onDragStart) {
      onDragStart(event, elementData);
    } else {
      event.dataTransfer.setData('application/json', JSON.stringify(elementData));
      event.dataTransfer.effectAllowed = 'copy';
    }
  };

  // Handle double-click on template to view its components
  const handleDoubleClick = (event, template) => {
    event.preventDefault();
    event.stopPropagation();

    setSelectedTemplate(template);
    setShowTemplateComponents(true);

    console.log('Double-clicked template:', template.name);
  };

  // Handle back button to return to template list
  const handleBackToTemplates = () => {
    setShowTemplateComponents(false);
    setSelectedTemplate(null);
  };

  const handlePlayVideo = (e, templateId, previewUrl) => {
    e.stopPropagation();
    if (!previewUrl) return;

    if (playingVideo === templateId) {
      // Stop playing
      if (videoRefs.current[templateId]) {
        videoRefs.current[templateId].pause();
        videoRefs.current[templateId].currentTime = 0;
      }
      setPlayingVideo(null);
    } else {
      // Stop any currently playing video
      if (playingVideo && videoRefs.current[playingVideo]) {
        videoRefs.current[playingVideo].pause();
        videoRefs.current[playingVideo].currentTime = 0;
      }
      // Start playing new video - set state first, then play will happen via useEffect or autoPlay
      setPlayingVideo(templateId);
    }
  };

  // Effect to handle video playback when playingVideo changes
  React.useEffect(() => {
    if (playingVideo && videoRefs.current[playingVideo]) {
      const video = videoRefs.current[playingVideo];
      video.play().catch(err => console.log('Video play failed:', err));
    }
  }, [playingVideo]);

  // Extract component data from selected template
  const templateComponents = selectedTemplate?.components || [];

  // Data fetcher for infinite scroll of template components
  const fetchTemplateComponents = async (page, pageSize) => {
    try {
      // Calculate pagination
      const startIndex = (page - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      
      // Slice the components for this page
      const pageComponents = templateComponents.slice(startIndex, endIndex);
      
      return {
        data: pageComponents,
        hasMore: endIndex < templateComponents.length,
      };
    } catch (error) {
      console.error('Error fetching template components:', error);
      return { data: [], hasMore: false };
    }
  };

  // Render function for each template item in the list
  const renderTemplateItem = (template, index) => (
    <div
      key={template.id || `template-${index}`}
      draggable
      onDragStart={(e) => handleDragStart(e, template)}
      onDoubleClick={(e) => handleDoubleClick(e, template)}
      onMouseEnter={() => setHoveredTemplate(template.id)}
      onMouseLeave={() => setHoveredTemplate(null)}
      className="group/card bg-ag-black-600 rounded-lg overflow-hidden cursor-grab active:cursor-grabbing transition-colors select-none relative"
      title="Drag to canvas or double-click to preview"
    >
      <div className="aspect-video bg-gray-300 flex items-center justify-center relative overflow-hidden">
        {playingVideo === template.id && template.previewUrl ? (
          <video
            crossOrigin='anonymous'
            ref={el => videoRefs.current[template.id] = el}
            className="w-full h-full object-cover"
            src={template.previewUrl}
            loop
            playsInline
            autoPlay
          />
        ) : template.thumbnail ? (
          <>
            <img
              crossOrigin='anonymous'
              src={template.thumbnail}
              alt={template.name || "Template"}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.style.display = "none";
                e.target.nextSibling.style.display = "flex";
              }}
            />
            <div className="hidden items-center justify-center w-full h-full absolute inset-0">
              <Image size={24} className="text-gray-500" />
            </div>
          </>
        ) : (
          <div className="flex items-center justify-center w-full h-full">
            <Image size={24} className="text-gray-500" />
          </div>
        )}
        
        {/* Play button overlay - always visible if preview URL exists */}
        {template.previewUrl && (
          <button
            onClick={(e) => handlePlayVideo(e, template.id, template.previewUrl)}
            className="absolute top-2 right-2 w-10 h-10 rounded-full bg-ag-green/90 hover:bg-ag-green border-2 border-ag-green text-ag-black-800 flex items-center justify-center transition-all hover:scale-110 shadow-lg shadow-ag-green/30 z-10"
            aria-label={playingVideo === template.id ? "Pause preview" : "Play preview"}
          >
            {playingVideo === template.id ? (
              <Pause size={16} fill="currentColor" />
            ) : (
              <Play size={16} fill="currentColor" className="ml-0.5" />
            )}
          </button>
        )}

        {/* Title & details overlay - only visible on hover */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent pt-6 px-3 pb-2 opacity-0 group-hover/card:opacity-100 transition-opacity duration-300 z-10">
          <h4 className="text-sm font-medium text-white mb-1 truncate">
            {template.name}
          </h4>
          <div className="flex justify-between items-center">
            <span className="text-xs text-gray-400">{template.category}</span>
            <span className="text-xs text-gray-400 text-right">
              {template.canvas?.name || `${template.canvas?.width || 1920} × ${template.canvas?.height || 1080}`}
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  // Custom render function for template preview at the top of component view
  const renderTemplatePreview = () => (
    <div
      className="col-span-full bg-ag-black-600 rounded-lg p-4 mb-2"
      draggable
      onDragStart={(e) => handleDragStart(e, selectedTemplate)}
    >
      <div className="aspect-video bg-gray-300 rounded mb-3 flex items-center justify-center overflow-hidden">
        {selectedTemplate.thumbnail ? (
          <>
            <img
              crossOrigin='anonymous'
              src={selectedTemplate.thumbnail}
              alt={selectedTemplate.name}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div className="hidden items-center justify-center w-full h-full">
              <Image size={32} className="text-gray-500" />
            </div>
          </>
        ) : (
          <div className="flex items-center justify-center w-full h-full">
            <Image size={32} className="text-gray-500" />
          </div>
        )}
      </div>
      <p className="text-xs text-gray-400 text-center">
        {selectedTemplate.description || 'Template Preview'}
      </p>
    </div>
  );

  // Custom render function for component items
  const renderComponentItem = (component, index, handleDragStart, handleMouseEnter, handleMouseLeave) => {
    // Use currSrc for preview (the rendered low-res version), fallback to preview or url
    const previewUrl = component.currSrc || component.preview || component.url;
    
    return (
      <div
        key={component.id || index}
        draggable
        onDragStart={(e) => handleDragStart(e, component)}
        onDoubleClick={() => onItemDoubleClick && onItemDoubleClick(component)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className="bg-ag-black-600 rounded-lg p-2 hover:bg-ag-black-500 cursor-grab active:cursor-grabbing transition-colors"
      >
        <div className="aspect-video bg-gray-800 rounded mb-2 flex items-center justify-center overflow-hidden">
          {previewUrl && previewUrl.match(/\.(mp4|webm|mov)$/i) ? (
            <video
              crossOrigin='anonymous'
              src={previewUrl}
              className="w-full h-full object-cover"
              muted
              loop
              playsInline
              preload="metadata"
            />
          ) : (
            <Image size={20} className="text-gray-500" />
          )}
        </div>
        <p className="text-xs text-white truncate">{component.name}</p>
        <p className="text-xs text-gray-500">{component.type || 'Component'}</p>
      </div>
    );
  };

  // Render template component view
  if (showTemplateComponents && selectedTemplate) {
    // Show empty state if no components
    if (templateComponents.length === 0) {
      return (
        <div className="h-full flex flex-col">
          {/* Back button */}
          <div className="mb-4">
            <button
              onClick={handleBackToTemplates}
              className="flex items-center gap-2 p-2 hover:bg-ag-black-600 rounded-lg transition-colors"
              title="Back to templates"
            >
              <ArrowLeft size={16} className="text-white" />
              <span className="text-sm text-white">Back to Templates</span>
            </button>
          </div>
          
          {renderTemplatePreview()}
          <div className="flex-1 flex items-center justify-center text-center py-8 text-gray-400">
            <p>No components available in this template</p>
          </div>
        </div>
      );
    }

    return (
      <div className={`h-full flex flex-col ${className}`}>
        {/* Back button */}
        <div className="mb-4">
          <button
            onClick={handleBackToTemplates}
            className="flex items-center gap-2 p-2 hover:bg-ag-black-600 rounded-lg transition-colors"
            title="Back to templates"
          >
            <ArrowLeft size={16} className="text-white" />
            <span className="text-sm text-white">Back to Templates</span>
          </button>
        </div>

        {/* Template preview at the top */}
        {renderTemplatePreview()}

        {/* Components header */}
        <h4 className="text-sm font-medium text-white mb-3 px-2">
          Available Components ({templateComponents.length})
        </h4>

        {/* Components grid with infinite scroll */}
        <div className="flex-1 min-h-0">
          <MediaRenderer
            key={selectedTemplate.id}
            dataFetcher={fetchTemplateComponents}
            itemType="component"
            gridCols={2}
            pageSize={10}
            dependencies={[selectedTemplate.id]}
            endMessage="No more components to load"
            showSize={false}
            showDuration={false}
            showType={true}
            onDragStart={handleDragStart}
            onItemDoubleClick={onItemDoubleClick}
            renderCustomItem={renderComponentItem}
          />
        </div>
      </div>
    );
  }

  // Render template list view
  // Show loading state
  if (templatesLoading) {
    return (
      <div className="text-center py-8 text-gray-400">
        Loading templates...
      </div>
    );
  }

  // Check for an active search with no results
  if (searchQuery && filteredTemplates.length === 0) {
    return (
      <div className="text-center py-8 text-gray-400">
        No templates found for "{searchQuery}"
      </div>
    );
  }

  // Show empty state if no templates
  if (templateItems.length === 0) {
    return (
      <div className="text-center py-8 text-gray-400">
        No templates available
      </div>
    );
  }

  return (
    <div className={`h-full ${className}`}>
      <InfiniteScrollContainer
        key={searchQuery}
        className="h-full"
        initialData={filteredTemplates}
        renderItem={renderTemplateItem}
        renderGrid={true}
        gridCols={1}
        pageSize={8}
      />
    </div>
  );
};

export default React.memo(TemplateComponents);